import { useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  apiErrorText,
  useCreatePayRequestMutation,
  useRecognizePayInvoiceMutation,
  type PayInvoiceFields,
  type PayRequestMeta,
  type UploadProgress,
} from '@shared/api';
import { formatBytes } from '@shared/lib/formatFile';
import { AnalysisBar } from '@shared/ui/AnalysisBar';
import { Button } from '@shared/ui/Button';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { Input, Select, TextArea } from '@shared/ui/Input';
import { Progress } from '@shared/ui/Progress';
import { IconChevronDown, IconChevronRight, IconFile, IconSend } from '@shared/ui/icons';
import {
  BANK_FIELDS,
  EMPTY_FORM,
  INVOICE_ACCEPT,
  charCount,
  checkForm,
  compact,
  departmentChoice,
  fitsAsInvoice,
  formFromInvoice,
  invoiceFileProblem,
  payloadFromForm,
  previewKind,
  todayIso,
  type PayForm,
  type PayFormField,
} from './model';
import { ObjectSelect, usePayObjects } from './ui/ObjectSelect';
import {
  Actions,
  CheckLabel,
  ErrorText,
  FieldBox,
  FieldCaption,
  FieldNote,
  Muted,
  Notice,
  SectionCard,
  SectionHead,
  SectionTitle,
  Small,
} from './ui/styled';

const Layout = styled.div<{ $preview: boolean }>`
  display: grid;
  grid-template-columns: ${({ $preview }) =>
    $preview ? 'minmax(0, 1.15fr) minmax(0, 1fr)' : 'minmax(0, 1fr)'};
  gap: ${({ theme }) => theme.spacing(5)};
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Step = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};

  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

/** Банковские реквизиты — вложенная сетка во всю ширину формы. */
const BankGrid = styled(FormGrid)`
  grid-column: 1 / -1;
`;

const Divider = styled.div`
  grid-column: 1 / -1;
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding-top: ${({ theme }) => theme.spacing(3)};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FileLine = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 14px;

  & > svg {
    flex: none;
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

const FileName = styled.span`
  flex: 1 1 200px;
  min-width: 0;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const ValueText = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 40px;
`;

const Counter = styled.span<{ $over: boolean }>`
  float: right;
  font-variant-numeric: tabular-nums;
  color: ${({ theme, $over }) => ($over ? theme.colors.status.danger : theme.colors.text.muted)};
`;

const BankToggle = styled.button`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `${theme.spacing(3)} 0 0`};
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: none;
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

const BankSummary = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const PreviewBox = styled.div`
  position: sticky;
  top: 0;
  height: min(82vh, 960px);
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};

  @media (max-width: 1100px) {
    position: static;
    height: 70vh;
  }
`;

const PreviewFrame = styled.iframe`
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
`;

const PreviewImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

/** Поля по порядку на экране — к первому с ошибкой уводим фокус. */
const FIELD_ORDER: PayFormField[] = [
  'service_id',
  'counterparty_name',
  'counterparty_inn',
  'counterparty_kpp',
  'invoice_number',
  'invoice_date',
  'amount',
  'vat_amount',
  'purpose',
  'category',
  'desired_pay_date',
  'project_id',
  'comment',
  'recipient_account_number',
  'recipient_bik',
  'recipient_bank_name',
  'recipient_corr_account',
];

const fieldId = (k: PayFormField) => `pay-request-${k}`;

/** Своё в форме, чего нет в счёте: при смене файла остаётся. */
function keepOwn(f: PayForm): PayForm {
  return {
    ...EMPTY_FORM,
    service_id: f.service_id,
    category: f.category,
    urgent: f.urgent,
    desired_pay_date: f.desired_pay_date,
    project_id: f.project_id,
    comment: f.comment,
  };
}

/** Ссылка на выбранный файл для предпросмотра; отзывается при смене файла. */
function useObjectUrl(file: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return undefined;
    }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url;
}

type Stage =
  | { kind: 'pick' }
  /** `uploaded` — процент передачи; `null` — файл на сервере, его читают. */
  | { kind: 'reading'; file: File; uploaded: number | null; since: number | null }
  | { kind: 'form'; file: File };

/** Что сказало чтение счёта. `manual` — человек не стал ждать. */
interface Reading {
  textFound: boolean;
  warnings: string[];
  vatRate: number | null;
  manual: boolean;
}

interface FieldProps {
  field: PayFormField;
  label: ReactNode;
  error?: string;
  hint?: ReactNode;
  wide?: boolean;
  children: ReactNode;
}

function Field({ field, label, error, hint, wide, children }: FieldProps) {
  const id = fieldId(field);
  return (
    <FieldBox $wide={wide}>
      <FieldCaption htmlFor={id}>{label}</FieldCaption>
      {children}
      {(error || hint) && (
        <FieldNote id={`${id}-note`} $error={Boolean(error)}>
          {error ?? hint}
        </FieldNote>
      )}
    </FieldBox>
  );
}

interface Props {
  meta: PayRequestMeta;
  onCancel: () => void;
}

/**
 * Новая заявка на оплату. Шаг 1 — приложить счёт: ОСНОВА сразу читает его
 * (скан — до минуты). Шаг 2 — проверить заполненные поля рядом с самим
 * счётом и отправить: заявка встаёт к Финансисту в реестр, открывается её
 * карточка.
 */
export function NewPayRequest({ meta, onCancel }: Props) {
  const navigate = useNavigate();
  const [recognize] = useRecognizePayInvoiceMutation();
  const [create, { isLoading: sending }] = useCreatePayRequestMutation();
  const dept = useMemo(() => departmentChoice(meta), [meta]);
  const objects = usePayObjects();

  const [stage, setStage] = useState<Stage>({ kind: 'pick' });
  const [pickError, setPickError] = useState<string | null>(null);
  const [reading, setReading] = useState<Reading | null>(null);
  const [form, setForm] = useState<PayForm>(() => ({
    ...EMPTY_FORM,
    service_id: dept.kind === 'choose' ? dept.preset : '',
  }));
  // Поля, где ошибку показываем сразу: заполнены по счёту или их уже трогали.
  const [checked, setChecked] = useState<ReadonlySet<PayFormField>>(new Set());
  const [submitTried, setSubmitTried] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  const [sendProgress, setSendProgress] = useState<number | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const pending = useRef<ReturnType<typeof recognize> | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      pending.current?.abort();
    };
  }, []);

  const readingSince = stage.kind === 'reading' ? stage.since : null;
  useEffect(() => {
    if (readingSince == null) return undefined;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [readingSince]);

  const file = stage.kind === 'pick' ? null : stage.file;
  const preview = file ? previewKind(file.name) : null;
  const previewUrl = useObjectUrl(preview ? file : null);

  const today = todayIso();
  const errors = checkForm(form, {
    needDepartment: dept.kind === 'choose',
    maxPurpose: meta.max_purpose_chars,
    today,
  });
  const errorOf = (k: PayFormField) => (submitTried || checked.has(k) ? errors[k] : undefined);
  const bankShown = bankOpen || BANK_FIELDS.some((k) => errorOf(k));

  const set = <K extends PayFormField>(k: K, v: PayForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const touch = (k: PayFormField) => setChecked((s) => (s.has(k) ? s : new Set([...s, k])));
  /** Общие свойства поля: id, связь с подсказкой, проверка после ухода из поля. */
  const a11y = (k: PayFormField, hasHint = false) => ({
    id: fieldId(k),
    'aria-invalid': errorOf(k) ? true : undefined,
    'aria-describedby': errorOf(k) || hasHint ? `${fieldId(k)}-note` : undefined,
    onBlur: () => touch(k),
  });

  async function readInvoice(picked: File) {
    const problem = invoiceFileProblem(picked, meta.max_file_mb);
    setPickError(problem);
    if (problem) return;
    pending.current?.abort();
    setStage({ kind: 'reading', file: picked, uploaded: 0, since: null });
    const onProgress = (p: UploadProgress) => {
      if (!alive.current) return;
      setStage((s) => {
        if (s.kind !== 'reading' || s.file !== picked) return s;
        if (p.phase === 'upload') {
          const pct = p.total > 0 ? Math.round((p.loaded / p.total) * 100) : 0;
          return { ...s, uploaded: pct };
        }
        return s.since == null ? { ...s, uploaded: null, since: Date.now() } : s;
      });
    };
    const req = recognize({ file: picked, onProgress });
    pending.current = req;
    let fields: PayInvoiceFields | null = null;
    let result: Reading;
    try {
      const res = await req.unwrap();
      fields = res.fields;
      result = {
        textFound: res.text_found,
        warnings: res.fields.warnings,
        vatRate: res.fields.vat_rate,
        manual: false,
      };
    } catch (err) {
      result = {
        textFound: false,
        warnings: [`${apiErrorText(err, 'Счёт не прочитан.')} Заполните поля по счёту.`],
        vatRate: null,
        manual: false,
      };
    }
    // Не дождались (заполнить вручную, другой файл, ушли со страницы) — молчим.
    if (pending.current !== req || !alive.current) return;
    pending.current = null;
    const base = keepOwn(form);
    if (fields) {
      const { form: next, filled } = formFromInvoice(fields, base);
      setForm(next);
      setChecked(new Set(filled));
      setBankOpen(false);
    } else {
      setForm(base);
      setChecked(new Set());
    }
    setReading(result);
    setSubmitTried(false);
    setSendError(null);
    setStage({ kind: 'form', file: picked });
  }

  function fillManually() {
    if (stage.kind !== 'reading') return;
    const req = pending.current;
    pending.current = null;
    req?.abort();
    setForm(keepOwn(form));
    setChecked(new Set());
    setReading({ textFound: false, warnings: [], vatRate: null, manual: true });
    setStage({ kind: 'form', file: stage.file });
  }

  function changeFile() {
    const req = pending.current;
    pending.current = null;
    req?.abort();
    setPickError(null);
    setSendError(null);
    setStage({ kind: 'pick' });
  }

  function onDropCapture(e: DragEvent<HTMLDivElement>) {
    // Общая зона молча пропускает неподходящий файл — объясняем сами.
    const dropped = Array.from(e.dataTransfer.files ?? []);
    const first = dropped[0];
    if (first && !dropped.some((f) => fitsAsInvoice(f.name))) {
      setPickError(invoiceFileProblem(first, meta.max_file_mb));
    }
  }

  async function submit() {
    if (stage.kind !== 'form' || sending) return;
    setSubmitTried(true);
    setSendError(null);
    const firstBad = FIELD_ORDER.find((k) => errors[k]);
    if (firstBad) {
      if (BANK_FIELDS.includes(firstBad)) setBankOpen(true);
      // Банковский блок мог быть свёрнут: фокус — после того, как он раскроется.
      window.requestAnimationFrame(() => document.getElementById(fieldId(firstBad))?.focus());
      return;
    }
    const payload = payloadFromForm(form, { sendDepartment: dept.kind === 'choose' });
    setSendProgress(0);
    try {
      const detail = await create({
        file: stage.file,
        payload,
        onProgress: (p) => {
          if (!alive.current) return;
          setSendProgress(
            p.phase === 'upload' && p.total > 0 ? Math.round((p.loaded / p.total) * 100) : 100,
          );
        },
      }).unwrap();
      // Вместо формы в истории — карточка: «назад» из неё ведёт к списку, а не к пустой форме.
      navigate(`/pay-requests/${detail.request.id}`, { replace: true, state: { created: true } });
    } catch (err) {
      if (alive.current) setSendError(apiErrorText(err, 'Заявка не отправлена.'));
    } finally {
      if (alive.current) setSendProgress(null);
    }
  }

  const purposeLength = charCount(form.purpose.trim());
  const account = compact(form.recipient_account_number);
  // Блок свёрнут после чтения счёта — банк виден в сводке, чтобы его можно было сверить.
  const bankName = form.recipient_bank_name.trim();
  const bankSummary = account
    ? `${bankName ? `${bankName}, ` : ''}р/с …${account.slice(-4)}${
        form.recipient_bik ? `, БИК ${compact(form.recipient_bik)}` : ''
      }`
    : form.recipient_bik || form.recipient_bank_name
      ? 'заполнены не полностью'
      : 'не указаны';
  const hasErrors = Object.keys(errors).length > 0;
  // Пустое поле НДС — «в счёте не указан», это не то же, что «без НДС».
  const vatHint = form.without_vat
    ? 'Счёт без НДС'
    : reading?.vatRate != null
      ? `Ставка в счёте — ${reading.vatRate} %`
      : form.vat_amount.trim()
        ? 'Сумма НДС из счёта'
        : 'Пусто — НДС в счёте не указан';

  const fileLine = file && (
    <FileLine>
      <IconFile size={18} />
      <FileName>{file.name}</FileName>
      <Small>{formatBytes(file.size)}</Small>
      <Button type="button" $variant="ghost" $size="sm" onClick={changeFile} disabled={sending}>
        Выбрать другой файл
      </Button>
    </FileLine>
  );

  return (
    <Layout $preview={Boolean(previewUrl)}>
      <SectionCard>
        <SectionHead>
          <div>
            <Step>{stage.kind === 'form' ? 'Шаг 2 из 2' : 'Шаг 1 из 2'}</Step>
            <SectionTitle>
              {stage.kind === 'form'
                ? 'Проверьте поля и отправьте Финансисту'
                : 'Приложите счёт на оплату'}
            </SectionTitle>
          </div>
          <Button type="button" $variant="ghost" $size="sm" onClick={onCancel} disabled={sending}>
            Отмена
          </Button>
        </SectionHead>

        {stage.kind === 'pick' && (
          <>
            <Muted>
              ОСНОВА сама прочитает из счёта поставщика, ИНН, сумму, НДС и банковские реквизиты —
              останется проверить и отправить.
            </Muted>
            <div onDropCapture={onDropCapture}>
              <FileDropZone
                accept={INVOICE_ACCEPT}
                primary="Перетащите сюда счёт на оплату"
                hint={`или нажмите, чтобы выбрать файл: PDF, фото или скан, Excel или Word — до ${meta.max_file_mb} МБ`}
                onFile={(f) => void readInvoice(f)}
              />
            </div>
            {pickError && <ErrorText role="alert">{pickError}</ErrorText>}
          </>
        )}

        {stage.kind === 'reading' && (
          <>
            {fileLine}
            {stage.uploaded != null ? (
              <>
                <AnalysisBar
                  title={`Загружаем счёт… ${stage.uploaded} %`}
                  hint="Файл передаётся на сервер."
                />
                <Progress percent={stage.uploaded} />
              </>
            ) : (
              <AnalysisBar
                title={`Читаем счёт…${
                  stage.since != null
                    ? ` ${Math.max(0, Math.round((now - stage.since) / 1000))} с`
                    : ''
                }`}
                hint="Ищем поставщика, ИНН, сумму, НДС и реквизиты банка. Скан или фото читаются дольше — до минуты."
              />
            )}
            <Actions>
              <Button type="button" $variant="secondary" onClick={fillManually}>
                Не ждать — заполнить вручную
              </Button>
            </Actions>
          </>
        )}

        {stage.kind === 'form' && (
          <>
            {fileLine}
            {reading && !reading.manual && reading.warnings.length > 0 && (
              <Notice $tone="warning">
                <strong>Проверьте:</strong>
                <ul>
                  {reading.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </Notice>
            )}
            {reading && reading.textFound && reading.warnings.length === 0 && (
              <Notice $tone="success">
                Поля заполнены по счёту — сверьте их с файлом
                {previewUrl ? ' рядом' : ''} и отправьте.
              </Notice>
            )}
            {reading?.manual && (
              <Notice $tone="info">
                Заполните поля по счёту — поля со звёздочкой обязательны.
              </Notice>
            )}

            <FormGrid>
              {dept.kind === 'fixed' && (
                <FieldBox>
                  <FieldCaption as="span">Отдел</FieldCaption>
                  <ValueText>{dept.title || 'Ваш отдел'}</ValueText>
                </FieldBox>
              )}
              {dept.kind === 'choose' && (
                <Field field="service_id" label="Отдел *" error={errorOf('service_id')}>
                  <Select
                    {...a11y('service_id')}
                    value={form.service_id}
                    onChange={(e) => set('service_id', e.target.value)}
                  >
                    <option value="">— выберите отдел —</option>
                    {dept.options.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.title}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              <FieldBox>
                <FieldCaption as="span">Заявитель</FieldCaption>
                <ValueText>{meta.full_name}</ValueText>
              </FieldBox>

              <Divider>Кому платим</Divider>
              <Field
                field="counterparty_name"
                label="Поставщик *"
                error={errorOf('counterparty_name')}
                wide
              >
                <Input
                  {...a11y('counterparty_name')}
                  value={form.counterparty_name}
                  onChange={(e) => set('counterparty_name', e.target.value)}
                  placeholder="ООО «Учебный центр»"
                  maxLength={255}
                />
              </Field>
              <Field field="counterparty_inn" label="ИНН" error={errorOf('counterparty_inn')}>
                <Input
                  {...a11y('counterparty_inn')}
                  value={form.counterparty_inn}
                  onChange={(e) => set('counterparty_inn', e.target.value)}
                  placeholder="10 или 12 цифр"
                  inputMode="numeric"
                  autoComplete="off"
                />
              </Field>
              <Field field="counterparty_kpp" label="КПП" error={errorOf('counterparty_kpp')}>
                <Input
                  {...a11y('counterparty_kpp')}
                  value={form.counterparty_kpp}
                  onChange={(e) => set('counterparty_kpp', e.target.value)}
                  placeholder="9 цифр, у ИП нет"
                  autoComplete="off"
                />
              </Field>

              <Divider>Счёт</Divider>
              <Field field="invoice_number" label="№ счёта" error={errorOf('invoice_number')}>
                <Input
                  {...a11y('invoice_number')}
                  value={form.invoice_number}
                  onChange={(e) => set('invoice_number', e.target.value)}
                  maxLength={60}
                />
              </Field>
              <Field field="invoice_date" label="Дата счёта">
                <Input
                  {...a11y('invoice_date')}
                  type="date"
                  value={form.invoice_date}
                  onChange={(e) => set('invoice_date', e.target.value)}
                />
              </Field>
              <Field
                field="amount"
                label="Сумма к оплате, ₽ *"
                error={errorOf('amount')}
                hint="Всего к оплате по счёту, с НДС"
              >
                <Input
                  {...a11y('amount', true)}
                  value={form.amount}
                  onChange={(e) => set('amount', e.target.value)}
                  placeholder="0,00"
                  inputMode="decimal"
                  autoComplete="off"
                />
              </Field>
              <FieldBox>
                <FieldCaption htmlFor={fieldId('vat_amount')}>НДС, ₽</FieldCaption>
                <Input
                  {...a11y('vat_amount', true)}
                  value={form.without_vat ? '' : form.vat_amount}
                  onChange={(e) => set('vat_amount', e.target.value)}
                  placeholder={form.without_vat ? 'без НДС' : 'сумма НДС из счёта'}
                  inputMode="decimal"
                  autoComplete="off"
                  disabled={form.without_vat}
                />
                <CheckLabel>
                  <input
                    type="checkbox"
                    checked={form.without_vat}
                    onChange={(e) => set('without_vat', e.target.checked)}
                  />
                  Без НДС
                </CheckLabel>
                <FieldNote
                  id={`${fieldId('vat_amount')}-note`}
                  $error={Boolean(errorOf('vat_amount'))}
                >
                  {errorOf('vat_amount') ?? vatHint}
                </FieldNote>
              </FieldBox>

              <Divider>За что и когда</Divider>
              <Field
                field="purpose"
                label={
                  <>
                    Назначение платежа *
                    <Counter aria-hidden="true" $over={purposeLength > meta.max_purpose_chars}>
                      {purposeLength} / {meta.max_purpose_chars}
                    </Counter>
                  </>
                }
                error={errorOf('purpose')}
                hint="Так оно попадёт в платёжку"
                wide
              >
                <TextArea
                  {...a11y('purpose', true)}
                  value={form.purpose}
                  onChange={(e) => set('purpose', e.target.value)}
                  placeholder="За что платим: «Оплата по счёту № 15 от 20.10.2026 за обучение по охране труда, в т.ч. НДС»"
                  rows={3}
                />
              </Field>
              <Field field="category" label="Статья расходов">
                <Select
                  {...a11y('category')}
                  value={form.category}
                  onChange={(e) => set('category', e.target.value)}
                >
                  <option value="">— не выбрана —</option>
                  {meta.categories.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                field="desired_pay_date"
                label="Желаемая дата оплаты"
                error={errorOf('desired_pay_date')}
              >
                <Input
                  {...a11y('desired_pay_date')}
                  type="date"
                  min={today}
                  value={form.desired_pay_date}
                  onChange={(e) => set('desired_pay_date', e.target.value)}
                />
              </Field>
              {objects.length > 0 && (
                <Field
                  field="project_id"
                  label="Объект"
                  hint="Если счёт по стройке — для бюджета объекта"
                >
                  <ObjectSelect
                    {...a11y('project_id', true)}
                    value={form.project_id}
                    objects={objects}
                    onChange={(id) => set('project_id', id)}
                  />
                </Field>
              )}
              <FieldBox>
                <FieldCaption as="span">Срочность</FieldCaption>
                <CheckLabel>
                  <input
                    type="checkbox"
                    checked={form.urgent}
                    onChange={(e) => set('urgent', e.target.checked)}
                  />
                  Срочно
                </CheckLabel>
                <FieldNote>Финансисту придёт уведомление о срочной заявке</FieldNote>
              </FieldBox>
              <Field
                field="comment"
                label="Комментарий для Финансиста"
                error={errorOf('comment')}
                wide
              >
                <TextArea
                  {...a11y('comment')}
                  value={form.comment}
                  onChange={(e) => set('comment', e.target.value)}
                  placeholder="Например: оплатить до 05.11 — иначе группу обучения перенесут"
                  rows={2}
                  maxLength={2000}
                />
              </Field>

              <BankToggle
                type="button"
                aria-expanded={bankShown}
                aria-controls="pay-request-bank"
                onClick={() => setBankOpen(!bankShown)}
              >
                {bankShown ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                Банковские реквизиты поставщика
                <BankSummary>— {bankSummary}</BankSummary>
              </BankToggle>
              {bankShown && (
                <BankGrid id="pay-request-bank">
                  <Field
                    field="recipient_account_number"
                    label="Расчётный счёт"
                    error={errorOf('recipient_account_number')}
                  >
                    <Input
                      {...a11y('recipient_account_number')}
                      value={form.recipient_account_number}
                      onChange={(e) => set('recipient_account_number', e.target.value)}
                      placeholder="20 цифр"
                      inputMode="numeric"
                      autoComplete="off"
                    />
                  </Field>
                  <Field field="recipient_bik" label="БИК банка" error={errorOf('recipient_bik')}>
                    <Input
                      {...a11y('recipient_bik')}
                      value={form.recipient_bik}
                      onChange={(e) => set('recipient_bik', e.target.value)}
                      placeholder="9 цифр"
                      inputMode="numeric"
                      autoComplete="off"
                    />
                  </Field>
                  <Field field="recipient_bank_name" label="Банк">
                    <Input
                      {...a11y('recipient_bank_name')}
                      value={form.recipient_bank_name}
                      onChange={(e) => set('recipient_bank_name', e.target.value)}
                    />
                  </Field>
                  <Field
                    field="recipient_corr_account"
                    label="Корреспондентский счёт"
                    error={errorOf('recipient_corr_account')}
                  >
                    <Input
                      {...a11y('recipient_corr_account')}
                      value={form.recipient_corr_account}
                      onChange={(e) => set('recipient_corr_account', e.target.value)}
                      placeholder="20 цифр, начинается с 301"
                      inputMode="numeric"
                      autoComplete="off"
                    />
                  </Field>
                </BankGrid>
              )}
            </FormGrid>

            {submitTried && hasErrors && (
              <ErrorText role="alert">
                Исправьте отмеченные поля — так заявку примут с первого раза.
              </ErrorText>
            )}
            {sendError && (
              <Notice $tone="danger" role="alert">
                {sendError}
              </Notice>
            )}
            <Actions>
              <Button
                type="button"
                $variant="primary"
                onClick={() => void submit()}
                disabled={sending}
              >
                <IconSend size={16} />
                {sending
                  ? sendProgress != null && sendProgress < 100
                    ? `Отправляем… ${sendProgress} %`
                    : 'Отправляем…'
                  : 'Отправить Финансисту'}
              </Button>
              <Button type="button" $variant="ghost" onClick={onCancel} disabled={sending}>
                Отмена
              </Button>
            </Actions>
          </>
        )}
      </SectionCard>

      {previewUrl && file && (
        <PreviewBox>
          {preview === 'pdf' ? (
            <PreviewFrame src={previewUrl} title={`Счёт: ${file.name}`} />
          ) : (
            <PreviewImage src={previewUrl} alt={`Счёт: ${file.name}`} />
          )}
        </PreviewBox>
      )}
    </Layout>
  );
}
